import * as companyService from "../../services/company";
import * as customerService from "../../services/customer";
import * as settings from "../../services/settings";

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  notification,
} from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePermission, useUser } from "../../hooks/usePermission";

import AuthGroup from "../../../configs/authgroup"; // นำเข้า permission จากไฟล์ mapping
import { ModalDelete } from "../../Components/model/modal/delete";
import dayjs from "dayjs";
import { exportComponent } from "../../Components/model/modal/export";
import { DATETIME_DISPLAY_FORMAT, formatDate } from "../../utils/date";
import permissions from "../../../configs/permission"; // นำเข้า permission จากไฟล์ mapping
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Link } = Typography;
const initPage = { limit: 10, page: 1 };
const { RangePicker } = DatePicker;

export const CustomerPage = () => {
  const hasInitialized = useRef(false);
  const { accessToken } = useAccessToken();
  const { companyId, saleId, customerId } = useUser();
  const { hasPermission, isSuperuser, hasGroup } = usePermission();
  const [fromSearch] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initPage);
  const [customerData, setCustomer] = useState([]);
  const { t } = useTranslation("customer", "buttons");
  const navigate = useNavigate();
  const { Title, Text } = Typography;
  const { Search } = Input;
  const [total, setTotal] = useState(0);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowsValues, setSelectedRowsValues] = useState([]);
  const [countSelect, setCountSelect] = useState(0);
  const [isOpenExport, setIsOpenExport] = useState(false);
  const [isExportAll, setIsExportAll] = useState(false);
  const [selectedSearchCredit, setSelectedSearchCredit] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [searchData, setSearchData] = useState("");
  const [searchSaleData, setSearchSaleData] = useState("");

  // search company and sales
  const [company, setCompany] = useState([]);

  const destinationWarehouseGroup = hasGroup(AuthGroup.DestinationWarehouse);
  const companySaleGroup = !isSuperuser && hasGroup(AuthGroup.CompanySale);

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({ message, description });
    },
    [api]
  );

  const defaultUserData = {
    company_id: companyId,
    sale_id: saleId,
    customer_id: customerId,
  };

  const [tempSearch, setTempSearch] = useState();
  const [mappingCompanyId, setMappingCompanyId] = useState(defaultUserData?.company_id);

  // CompanySale user ที่ผูกกับ customer_id เดียว → view-only scope
  const isLockedToSingleCustomer = companySaleGroup && defaultUserData?.customer_id != null;

  const isOwnedByCurrentSale = useCallback(
    (record) => {
      if (!companySaleGroup) return true;
      // Precedence: customer_id overrides sale_id scope
      if (defaultUserData?.customer_id != null) {
        const rowCustomerId = Number(record?.id);
        return Number.isFinite(rowCustomerId) && rowCustomerId === Number(defaultUserData.customer_id);
      }
      if (defaultUserData?.sale_id == null) return false;
      const rowSaleId = Number(record?.sale_id ?? record?.app_sale?.id);
      return Number.isFinite(rowSaleId) && rowSaleId === Number(defaultUserData.sale_id);
    },
    [companySaleGroup, defaultUserData?.sale_id, defaultUserData?.customer_id]
  );

  const columns = [
    {
      title: t("Customer_Table.Id"),
      dataIndex: "id",
      key: "id",
      width: "4%",
      render: (_, row) => (
        <Link
          style={{ cursor: "pointer" }}
          onClick={() => {
            navigate("/customer/create", {
              state: { type: "EDIT", customerData: row },
            });
          }}
        >
          {row.id}
        </Link>
      ),
    },
    {
      title: t("Customer_Table.Code"),
      dataIndex: "customer_code",
      key: "customer_code",
      width: "4%",
      render: (_, row) => (
        <p
          style={{ cursor: "pointer" }}
          onClick={() => {
            navigate("/customer/create", {
              state: { type: "EDIT", customerData: row },
            });
          }}
        >
          {row.customer_code}
        </p>
      ),
    },
    {
      title: t("Customer_Table.Name"),
      dataIndex: "customer_name",
      key: "customer_name",
      width: "5%",
    },
    {
      title: t("Customer_Table.Sale_Name"),
      dataIndex: "app_sale.id",
      key: "app_sale.sale_name",
      width: "5%",
      render: (_, record) => (
        <span>
          {record.app_sale.sale_name}
          <span>({record.app_company.company_code + " - " + record.app_sale.sale_name})</span>
        </span>
      ),
    },
    {
      title: t("Customer_Table.Sale_Customer_Code"),
      dataIndex: "app_sale.id",
      key: "app_sale.sale_customer_code",
      width: "7%",
      render: (_, record) => {
        if (!isOwnedByCurrentSale(record)) return <span>-</span>;
        return (
          <span>{record.app_company?.company_code + " - " + record.app_sale?.sale_name + record.customer_code}</span>
        );
      },
    },
    {
      title: t("Customer_Table.Route_Customer_Code"),
      dataIndex: "app_customerformula.id",
      key: "app_customerformula.id",
      width: "8%",
      render: (i, record) => (
        <span>
          {record.app_customerroute.map((element, index) => {
            let warehouse_code_destination =
              element.app_warehouse_app_customerroute_destination_idToapp_warehouse.warehouse_code ?? "";
            let warehouse_code_origin =
              element.app_warehouse_app_customerroute_origin_idToapp_warehouse.warehouse_code ?? "";

            const customerCode = `${record.app_company.company_code}-${record.app_sale.sale_name}${record.customer_code}(${warehouse_code_origin} -> ${warehouse_code_destination})`;
            return (
              <p key={index}>
                {customerCode}
                {index < record.app_customerroute.length - 1 ? ", " : ""}
              </p>
            );
          })}
        </span>
      ),
    },
    {
      title: t("Customer_Table.Company_Formula"),
      dataIndex: "app_customerformula.id",
      key: "app_customerformula.company_id",
      width: "8%",
      render: (i, record) => (
        <span>
          {record.app_company.company_code}-{record.app_sale.sale_name}
          {record.customer_code}
          {record.app_customerroute.map((element, index) => {
            let warehouse_code_destination =
              element.app_warehouse_app_customerroute_destination_idToapp_warehouse.warehouse_code ?? "";
            let warehouse_code_origin =
              element.app_warehouse_app_customerroute_origin_idToapp_warehouse.warehouse_code ?? "";

            const companyFormula = `(${warehouse_code_origin} -> ${warehouse_code_destination})`;
            return (
              <p key={index}>
                {companyFormula}
                {index < record.app_customerroute.length - 1 ? ", " : ""}
              </p>
            );
          })}
        </span>
      ),
    },
    {
      title: t("Customer_Table.Is_Credit"),
      dataIndex: "is_credit",
      key: "is_credit",
      width: "4%",
      render: (_, record) => {
        if (record.is_credit) {
          return (
            <p style={{ color: "green" }}>
              <CheckCircleOutlined />
            </p>
          );
        } else {
          return (
            <p style={{ color: "red" }}>
              <CloseCircleOutlined />
            </p>
          );
        }
      },
    },
    {
      title: t("Customer_Table.Tax"),
      dataIndex: "tax_id",
      key: "tax_id",
      width: "5%",
      render: (_, record) => <span>{record.tax_id ? record.tax_id : "-"}</span>,
    },
    {
      title: t("Customer_Table.Telephone"),
      dataIndex: "telephone",
      key: "telephone",
      width: "5%",
      render: (_, record) => <span>{record.telephone ? record.telephone : "-"}</span>,
    },
    {
      title: t("Customer_Table.Customer_Address"),
      dataIndex: "customer_address",
      key: "customer_address",
      width: "10%",
      render: (_, record) => <span>{record.customer_address ? record.customer_address : "-"}</span>,
    },
    {
      title: t("Customer_Table.Delivery_Address"),
      dataIndex: "delivery_address",
      key: "delivery_address",
      width: "10%",
      render: (_, record) => <span>{record.delivery_address ? record.delivery_address : "-"}</span>,
    },
    {
      title: t("Customer_Table.Email"),
      dataIndex: "email",
      key: "email",
      width: "6%",
      render: (_, record) => <span>{record.email ? record.email : "-"}</span>,
    },
    {
      title: t("Customer_Table.Create_Date"),
      dataIndex: "created_at",
      key: "created_at",
      width: "6%",
      render: (value) => formatDate(value, DATETIME_DISPLAY_FORMAT),
    },
    {
      title: t("Customer_Table.Edit"),
      dataIndex: "edit",
      key: "edit",
      width: "4%",
      render: (_, row) => {
        const disableByOwnership = companySaleGroup && !isOwnedByCurrentSale(row);
        return (
          <Button
            type="link"
            disabled={!hasPermission(permissions.Customer?.CHANGE) || disableByOwnership}
            onClick={() => {
              navigate("/customer/create", {
                state: { type: "EDIT", customerData: row },
              });
            }}
          >
            <EditOutlined />
          </Button>
        );
      },
    },
    {
      title: t("Customer_Table.Is_Delete"),
      dataIndex: "delete",
      key: "delete",
      width: "4%",
      render: (_, record) => {
        const disableByOwnership = companySaleGroup && !isOwnedByCurrentSale(record);
        // locked single-customer user must not delete their own record
        const disableBySelfLock = isLockedToSingleCustomer;
        return (
          <Button
            type="link"
            disabled={!hasPermission(permissions.Customer?.DELETE) || disableByOwnership || disableBySelfLock}
            onClick={() => {
              setIsOpenDelete(true);
              setPrepareDeleteData(record);
            }}
          >
            <DeleteOutlined />
          </Button>
        );
      },
    },
  ];

  const onDeleteCustomer = async (data) => {
    let prepareDeleteCustomer = {
      id: data.id,
    };
    try {
      const response = await customerService.deleteCustomer(accessToken, prepareDeleteCustomer);
      fetchCustomer({ ...search });
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"update Success"}</>);
      setLoading(false);
    } catch (err) {
      console.error("[CustomerPage] onDeleteCustomer: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
  };

  const normalizeSelection = (values) => {
    const modify = values !== "all" ? values : undefined;
    return modify;
  };

  const buildCustomerListFilters = (value) => {
    const { search, search_date, search_credit, search_company, search_sale } = value;
    let finalCompany = search_company;
    let finalSale = search_sale;
    if (!isSuperuser && !destinationWarehouseGroup) {
      finalCompany = defaultUserData?.company_id;
    }
    if (companySaleGroup && defaultUserData?.sale_id) {
      finalSale = defaultUserData?.sale_id;
    }

    const lockedCustomerId =
      companySaleGroup && defaultUserData?.customer_id != null ? Number(defaultUserData.customer_id) : undefined;

    let start_date;
    let end_date;

    if (search_date) {
      start_date = dateRange[0] || undefined;
      end_date = dateRange[1] || undefined;
    }

    const searchText = search != undefined ? search.trim() : null;
    return {
      start_date,
      end_date,
      customer_code: searchText,
      customer_name: searchText,
      telephone: searchText,
      company_id: normalizeSelection(finalCompany),
      sale_id: normalizeSelection(finalSale),
      is_credit: normalizeSelection(search_credit),
      is_superuser: isSuperuser,
      ...(lockedCustomerId != null ? { customer_id: lockedCustomerId } : {}),
    };
  };

  const prepareCustomerListRequestParams = useCallback(
    (params) => {
      if (!companySaleGroup) return params;
      const scopedCustomerId =
        companySaleGroup && defaultUserData?.customer_id != null ? Number(defaultUserData.customer_id) : undefined;
      const scopedSaleId =
        companySaleGroup && scopedCustomerId == null && defaultUserData?.sale_id != null
          ? Number(defaultUserData.sale_id)
          : undefined;
      if (scopedCustomerId != null) {
        const next = { ...params, customer_id: scopedCustomerId };
        delete next.sale_id;
        return next;
      }
      return { ...params, sale_id: scopedSaleId };
    },
    [companySaleGroup, defaultUserData?.sale_id, defaultUserData?.customer_id]
  );

  const fetchCustomer = useCallback(
    async (params) => {
      try {
        setLoading(true);

        // Deny-by-default: company_sale must have at least one scope (customer_id or sale_id)
        if (companySaleGroup && defaultUserData?.customer_id == null && defaultUserData?.sale_id == null) {
          setCustomer([]);
          setTotal(0);
          setSearch({ ...params });
          return;
        }

        // Precedence: customer_id overrides sale_id scope
        const scopedCustomerId =
          companySaleGroup && defaultUserData?.customer_id != null ? Number(defaultUserData.customer_id) : undefined;
        const scopedSaleId =
          companySaleGroup && scopedCustomerId == null && defaultUserData?.sale_id != null
            ? Number(defaultUserData.sale_id)
            : undefined;

        let requestParams = prepareCustomerListRequestParams(params);

        const customerList = await customerService.getCustomer(accessToken, requestParams);
        const rawCustomers = Array.isArray(customerList?.customers) ? customerList.customers : [];

        // Defense in depth: local filter with same precedence
        let scopedCustomers = rawCustomers;
        if (companySaleGroup) {
          scopedCustomers = rawCustomers.filter((item) => {
            if (scopedCustomerId != null) {
              const rowId = Number(item?.id);
              return Number.isFinite(rowId) && rowId === scopedCustomerId;
            }
            const rowSaleId = Number(item?.sale_id ?? item?.app_sale?.id);
            return Number.isFinite(rowSaleId) && rowSaleId === scopedSaleId;
          });

          const searchCustomerCode = String(requestParams?.customer_code ?? "")
            .trim()
            .toLowerCase();
          if (searchCustomerCode) {
            scopedCustomers = scopedCustomers.filter((item) =>
              String(item?.customer_code ?? "")
                .trim()
                .toLowerCase()
                .includes(searchCustomerCode)
            );
          }
        }

        setCustomer(scopedCustomers);
        setTotal(companySaleGroup ? scopedCustomers.length : (customerList?.totalCount ?? scopedCustomers.length));
        setSearch({ ...requestParams });
      } catch (err) {
        console.error("[CustomerPage] fetchCustomer: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, companySaleGroup, defaultUserData?.sale_id, defaultUserData?.customer_id, openNotificationWithIcon, prepareCustomerListRequestParams]
  );

  const getCompanyList = useCallback(async () => {
    try {
      setLoading(true);
      const companyList = await companyService.getCompanysList(accessToken);
      setCompany(companyList);
    } catch (err) {
      console.error("[CustomerPage] fetchCustomerCompany: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const getCompanyById = useCallback(
    async (id) => {
      try {
        let companyList = [];
        setLoading(true);
        const data = await companyService.getCompanyById(accessToken, id);
        companyList.push(data);
        setCompany(companyList);
      } catch (err) {
        console.error("[CustomerPage] fetchCustomerCompany: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  const mappingCompanyOption = async () => {
    if (isSuperuser || destinationWarehouseGroup) {
      await getCompanyList();
    } else if (defaultUserData?.company_id) {
      await getCompanyById(defaultUserData?.company_id);
    }
  };

  const optionCompanySelection = (companies = []) => {
    const hasMultiple = companies.length > 1;

    return [
      ...(hasMultiple ? [{ label: "All Company", value: "all" }] : []),
      ...companies.map((item, index) => ({
        label: `${item.company_name} (${item.company_code})`,
        value: item.id,
        key: `company-${index}-${item.id}`,
      })),
    ];
  };

  const optionSaleSelection = (companies = []) => {
    return [
      { label: "All Sale", value: "all" },
      ...companies
        .filter((company) => company.id === mappingCompanyId)
        .flatMap((company) =>
          (company.app_sale || []).map((sale, sIndex) => ({
            label: `${sale.sale_name} (${company.company_code}-${sale.sale_name})`,
            value: sale.id,
            key: `sale-${company.id}-${sIndex}-${sale.id}`,
          }))
        ),
    ];
  };

  const handleChangeCompany = (value, _) => {
    setMappingCompanyId(value);
  };

  const onSearch = async (value) => {
    const params = buildCustomerListFilters(value);
    await fetchCustomer({ ...params, page: value.page ?? initPage.page, limit: value.limit ?? initPage.limit });
  };

  const onFinish = async () => {
    const value = fromSearch.getFieldsValue();
    try {
      setTempSearch(value);
      setLoading(true);
      await onSearch(value);
    } finally {
      setLoading(false);
    }
  };

  const onReset = async () => {
    fromSearch.resetFields();
    try {
      setCountSelect(0);
      setLoading(true);
      await onSearch({ ...defaultUserData, ...initPage });
    } finally {
      setLoading(false);
    }
  };

  const onSelectAll = async () => {
    setCountSelect(total);
    setIsExportAll(true);
    setIsOpenExport(total.length > 0 ? true : false);
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
  };

  const onExport = async () => {
    // Define custom headers with corresponding keys
    const headerMap = [
      { label: t("Customer_Export_Excel.Customer_Name"), key: "customer_name" },
      { label: t("Customer_Export_Excel.Company_Code"), key: "company_code" },
      { label: t("Customer_Export_Excel.Sale_Code"), key: "sale_code" },
      { label: t("Customer_Export_Excel.Customer_Code"), key: "customer_code" },
      {
        label: t("Customer_Export_Excel.Sale_Customer_Code"),
        key: "sale_customer_code",
      },
      {
        label: t("Customer_Export_Excel.Customer_Routes"),
        key: "customer_route_code",
      },
      { label: t("Customer_Export_Excel.Start_Date"), key: "start_date" },
      {
        label: t("Customer_Export_Excel.Customer_Address"),
        key: "customer_address",
      },
      {
        label: t("Customer_Export_Excel.Delivery_Address"),
        key: "delivery_address",
      },
      { label: t("Customer_Export_Excel.Telephone"), key: "telephone" },
      { label: t("Customer_Export_Excel.Tax_Id"), key: "tax_id" },
      { label: t("Customer_Export_Excel.Is_Credit"), key: "is_credit" },
    ];
    const fileName = `customer-${dayjs().format("YYYYMMDD")}${dayjs().format("HHmmss")}`;
    let formattedData;
    setIsOpenExport(true);
    if (isExportAll) {
      const formValue = tempSearch ?? fromSearch.getFieldsValue();
      const filters = buildCustomerListFilters(formValue);
      const params = prepareCustomerListRequestParams({
        page: 1,
        limit: total,
        ...filters,
      });
      try {
        const response = await customerService.exportCustomer(accessToken, params);
        // สร้างลิงก์สำหรับดาวน์โหลดไฟล์
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName + ".xlsx"); // ชื่อไฟล์
        document.body.appendChild(link);
        link.click();
        // ทำความสะอาดลิงก์
        link.parentNode.removeChild(link);
        setIsOpenExport(false);
      } catch (err) {
        console.error("[CustomerPage] onExport: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
    } else {
      formattedData = selectedRowsValues.map((item) => {
        return {
          customer_name: item.customer_name || "-",
          company_code: item.app_company?.company_code || "-",
          sale_code: item.app_sale?.sale_name || "-",
          customer_code: item.customer_code || "-",
          sale_customer_code: generateSaleCustomerCode(item),
          customer_route_code: generateCustomerRouteCode(item),
          start_date: item.created_at ? dayjs(item.created_at).format("YYYY-MM-DD") : "-",
          customer_address: item.customer_address || "-",
          delivery_address: item.delivery_address || "-",
          telephone: item.telephone || "-",
          tax_id: item.tax_id || "-",
          is_credit: item.is_credit ? "เครดิต" : "เงินสด",
        };
      });
    }
    exportComponent({ formattedData, fileName, headerMap });
    setIsOpenExport(false);
  };

  const generateSaleCustomerCode = (item) => {
    return item.app_company?.company_code && item.app_sale?.sale_name && item.customer_code
      ? `${item.app_company.company_code}-${item.app_sale.sale_name}${item.customer_code}`
      : "-";
  };

  const generateCustomerRouteCode = (item) => {
    return item.app_customerroute
      ? item.app_customerroute
          .map((route) => {
            const destination =
              route.app_warehouse_app_customerroute_destination_idToapp_warehouse?.warehouse_code || "";
            const origin = route.app_warehouse_app_customerroute_origin_idToapp_warehouse?.warehouse_code || "";
            return `${item.app_company?.company_code || ""} - ${
              item.app_sale?.sale_name || ""
            }${item.customer_code || ""} (${origin} -> ${destination})`;
          })
          .join(", ")
      : "-";
  };

  const handleDateChange = (dates, dateStrings) => {
    setDateRange(dateStrings);
  };

  const items = [
    {
      title: <span>{t("Page_Body_Tab_Menu.Home", { ns: "menus" })}</span>,
    },
    {
      title: <span>{t("Page_Body_Tab_Menu.Menu", { ns: "menus" })}</span>,
    },
    {
      title: (
        <a href="/customer" style={{ fontWeight: 600, color: "black" }}>
          {t("Title_Customer")}
        </a>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    columnWidth: "2%",
    onChange: (selectedRowKeys, value) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRowsValues(value);
      setCountSelect(value.length);
      setIsOpenExport(selectedRowKeys.length > 0 ? false : true);
      setIsExportAll(false);
    },
  };

  const handleSearch = async (value) => {
    if (!value) {
      setSearchData("");
    } else if (value !== searchData) {
      setSearchData("");
    }
    setSearchData(value);
    await filterCompanySale(value);
  };

  const filterOption = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const disableFutureDates = (current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  useEffect(() => {
    if (!accessToken || hasInitialized.current) return;
    hasInitialized.current = true;

    setIsOpenExport(true);
    setCountSelect(0);
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
    if (!isSuperuser && defaultUserData?.company_id) {
      fromSearch.setFieldsValue({
        search_company: defaultUserData.company_id,
      });
    }
    if (companySaleGroup && defaultUserData?.sale_id) {
      fromSearch.setFieldsValue({
        search_sale: defaultUserData.sale_id,
      });
    }
    (async () => {
      await Promise.all([fetchCustomer({ ...defaultUserData, ...initPage }), mappingCompanyOption()]);
    })();
  }, [accessToken, company]);

  const selectIsCredit = [
    {
      value: "all",
      label: "All Credit",
    },
    {
      value: "true",
      label: "Credit",
    },
    {
      value: "false",
      label: "Not Credit",
    },
  ];

  const handleCloseModal = () => setIsOpenDelete(false);

  // Final defense: strip rows that do not belong to the current CompanySale user
  const visibleCustomerData = useMemo(() => {
    if (!companySaleGroup) return customerData;
    if (defaultUserData?.sale_id == null) return [];
    return (customerData || []).filter(isOwnedByCurrentSale);
  }, [customerData, companySaleGroup, defaultUserData?.sale_id, isOwnedByCurrentSale]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row>
          <Col span={24}>
            <Breadcrumb items={items}></Breadcrumb>
            <Title
              level={4}
              style={{
                textWrap: "nowrap",
                display: "flex",
                gap: "7px",
                padding: "15px 0px",
              }}
            >
              <ArrowLeftOutlined style={{ fontSize: "20px" }} />
              {t("Title_Customer")}
            </Title>
          </Col>
        </Row>
        <Row style={{ display: "flex", justifyContent: "left", overflowX: "auto" }}>
          <Col span={12}>
            <Form
              form={fromSearch}
              labelCol={{
                span: 8,
              }}
              wrapperCol={{
                span: 16,
              }}
              labelAlign="left"
              layout="horizontal"
              onFinish={onFinish}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  fromSearch.submit();
                }
              }}
              initialValues={{
                search_date: [dayjs().startOf("day"), dayjs().endOf("day")],
                search_company: defaultUserData?.company_id ? defaultUserData.company_id : "all",
                // search_company: company.length > 1 ? "all" : defaultUserData?.company_id,
                search_sale: companySaleGroup && defaultUserData?.sale_id ? defaultUserData.sale_id : "all",
                search: undefined,
                search_credit: "all",
              }}
            >
              <Row flex={1}>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="search_date" label={t("Customer_Search.Create_At")}>
                    <RangePicker
                      allowEmpty
                      style={{ width: "100%", minWidth: 200 }}
                      onChange={handleDateChange}
                      disabledDate={disableFutureDates}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Customer_Search.Company") + ":"} name="search_company" key="search_company">
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      showSearch
                      onSearch={handleSearch}
                      placeholder={"Please Select Company"}
                      options={optionCompanySelection(company)}
                      filterOption={filterOption}
                      onSelect={handleChangeCompany}
                      disabled={companySaleGroup}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item
                    label={t("Customer_Search.Sales") + ":"}
                    name="search_sale"
                    key="search_sale"
                    style={{ marginBottom: "1rem" }}
                  >
                    <Select
                      showSearch
                      placeholder={"Please Select Sale"}
                      options={optionSaleSelection(company)}
                      filterOption={filterOption}
                      style={{ minWidth: 200 }}
                      disabled={companySaleGroup}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="search" label={t("Customer_Search.Search")}>
                    <Input
                      size="default"
                      placeholder="Customer Code / Customer Name / Telephone"
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Customer_Search.Is_Credit")} name="search_credit" key="search_credit">
                    <Select placeholder={"Please Select Credit"} options={selectIsCredit} style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" align="middle">
                <Col span={24} style={{ textAlign: "end" }}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      {t("BTN_Search", { ns: "buttons" })}
                    </Button>
                    <Button type="default" onClick={() => onReset()} style={{ marginLeft: "5px" }}>
                      {t("BTN_Reset", { ns: "buttons" })}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Space direction="inline">
              {hasPermission(permissions.Customer?.EXPORT) && (
                <Button type="primary" disabled={isOpenExport} onClick={onExport}>
                  {t("Button_Customer.BTN_Export_Excel")}
                </Button>
              )}
              {hasPermission(permissions.Customer?.ADD) && !isLockedToSingleCustomer && (
                <Button
                  type="primary"
                  onClick={() => {
                    navigate("/customer/create", {
                      state: { type: "CREATE" },
                    });
                  }}
                >
                  {t("Button_Customer.BTN_Add")}
                </Button>
              )}
            </Space>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Card>
              <Row align="middle" justify="center">
                <Col span={24}>
                  {`${t("Customer_Table.Header_Select")} : ${countSelect} ${t("Customer_Table.Header_To")} ${total}`}
                  &nbsp; &nbsp;
                  <Link onClick={onSelectAll} target="_blank">
                    {t("Customer_Table.Header_Select_All")}
                  </Link>
                </Col>
              </Row>
              <Table
                loading={loading}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={visibleCustomerData}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                pagination={{
                  defaultPageSize: search.limit,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  current: search.page,
                  pageSize: search.limit,
                  total: total,
                  onChange: (page, limit) => {
                    onSearch({ ...tempSearch, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>
        {hasPermission(permissions.Customer?.DELETE) && (
          <ModalDelete
            isOpen={isOpenDelete}
            handleCloseModal={handleCloseModal}
            onClick={() => {
              onDeleteCustomer(prepareDeleteData);
            }}
            dataDetail={prepareDeleteData}
            t={t}
          />
        )}
      </Spin>
    </>
  );
};
